import React from "react";

// Article 3 — Gallstones in Pregnancy: When Is Surgery Safer Than Waiting?
// Content is verbatim from the approved brief. Do not paraphrase, shorten, expand
// or add FAQs, sticky contents menu, ratings, comments, popularity counters or
// unrelated call-to-action. Medical-review line is withheld.

function Cite({ n }) {
  const first = String(n).split(",")[0];
  return (
    <sup className="text-prestige font-medium">
      <a
        href={`#ref-${first}`}
        aria-label={`See reference ${first}`}
        className="hover:underline"
      >{`[${n}]`}</a>
    </sup>
  );
}

const H2 = ({ children }) => (
  <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-12">
    {children}
  </h2>
);

const P = ({ children }) => (
  <p className="mt-4 text-[15px] text-stone2 leading-relaxed">{children}</p>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-3">
    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
    <span>{children}</span>
  </li>
);

const Bullets = ({ items }) => (
  <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
    {items.map((i) => (
      <Bullet key={i}>{i}</Bullet>
    ))}
  </ul>
);

export function GallstonesPregnancySurgery() {
  return (
    <article className="mt-10">
      <P>
        Pregnancy is not a reason to ignore repeated gallstone attacks. A single uncomplicated episode may settle with observation, hydration and appropriate pain relief, but <strong>infection, persistent obstruction or pancreatitis can make waiting more dangerous than treatment</strong>.
      </P>
      <P>The decision should be based on the diagnosis—not simply on the trimester.</P>

      <H2>Gallstones, Biliary Colic and Cholecystitis Are Not the Same</H2>
      <P>Gallstones may be found incidentally and cause no symptoms.</P>
      <P>
        <strong>Biliary colic</strong> usually causes steady pain in the right upper abdomen or upper middle abdomen, often after food. The pain may spread to the back or right shoulder and may be accompanied by nausea.
      </P>
      <P>
        <strong>Acute cholecystitis</strong> means inflammation of the gallbladder. Pain is usually more persistent and may occur with fever, tenderness and raised inflammatory markers.
      </P>
      <P>A stone may also move into the main bile duct and cause:</P>
      <Bullets
        items={[
          "Jaundice.",
          "Cholangitis—an infected, obstructed bile duct.",
          "Gallstone pancreatitis.",
        ]}
      />
      <P>
        These conditions require different treatment. Repeatedly calling every episode “gas” or “normal pregnancy acidity” can delay necessary care.
      </P>

      <H2>How Is the Diagnosis Confirmed?</H2>
      <P>
        Ultrasound is usually the first investigation. It can identify gallstones, gallbladder-wall inflammation, bile-duct dilatation and some alternative causes of pain.
      </P>
      <P>
        If the ultrasound is uncertain and a bile-duct stone remains possible, magnetic resonance cholangiopancreatography—<strong>MRCP</strong>—may define the biliary anatomy without ionising radiation.
      </P>
      <P>Blood tests may include:</P>
      <Bullets
        items={[
          "Full blood count.",
          "Liver enzymes and bilirubin.",
          "Lipase or amylase.",
          "Kidney function and electrolytes.",
          "Inflammatory markers.",
        ]}
      />
      <P>
        The obstetric assessment should also consider preeclampsia, HELLP syndrome, placental disease and other pregnancy-related causes of upper-abdominal pain.
      </P>

      <H2>When May Waiting Be Reasonable?</H2>
      <P>Observation may be reasonable after an uncomplicated episode when:</P>
      <Bullets
        items={[
          "The pain settles.",
          "There is no fever or jaundice.",
          "Blood tests are reassuring.",
          "There is no cholecystitis, pancreatitis or duct obstruction.",
          "The woman can eat and drink.",
          "Maternal and fetal assessments are reassuring.",
          "A clear follow-up and return plan exists.",
        ]}
      />
      <P>
        Treatment may include hydration, pregnancy-compatible pain relief, antiemetic treatment and temporary dietary adjustment.
      </P>
      <P>
        A very low-fat diet is not a cure and should not become nutritionally inadequate. The aim is to avoid obvious triggers while maintaining sufficient protein, energy and micronutrients.
      </P>

      <H2>When Is Surgery Safer Than Repeated Waiting?</H2>
      <P>
        The 2024 SAGES guideline suggests laparoscopic cholecystectomy rather than non-operative treatment for biliary disease during pregnancy, although the certainty of evidence is low. It more specifically favours surgery for acute cholecystitis. <Cite n="1" />
      </P>
      <P>Surgery becomes more compelling with:</P>
      <Bullets
        items={[
          "Acute cholecystitis.",
          "Recurrent attacks requiring hospital care.",
          "Gallstone pancreatitis.",
          "Inability to maintain hydration or nutrition.",
          "Persistent pain despite treatment.",
          "Complications from a blocked bile duct.",
          "Diagnostic concern that cannot be managed safely as an outpatient.",
        ]}
      />
      <P>
        Repeated conservative treatment may lead to another attack, another admission or a more serious complication.
      </P>
      <P>
        A 2025 international study of biliary pancreatitis found fewer recurrent biliary readmissions after cholecystectomy during pregnancy in women with mild disease, although the study was observational and the first-trimester evidence remained limited. <Cite n="4" />
      </P>

      <H2>Is Laparoscopy Safe During Pregnancy?</H2>
      <P>
        When gallbladder removal is required, the laparoscopic approach is generally preferred by experienced teams.
      </P>
      <P>Pregnancy changes:</P>
      <Bullets
        items={[
          "Abdominal anatomy.",
          "Patient positioning.",
          "Entry technique.",
          "Anaesthetic planning.",
          "Thrombosis risk.",
          "Fetal assessment before and after surgery.",
        ]}
      />
      <P>These changes require planning; they do not make necessary surgery automatically unsafe.</P>
      <P>
        ACOG states that medically necessary surgery should not be denied or delayed because of pregnancy, regardless of trimester. Elective surgery should wait until after birth. <Cite n="2" />
      </P>
      <P>
        The second trimester is often technically convenient, but it is not the only time surgery can be performed. In third-trimester biliary colic without cholecystitis, SAGES advises that either surgery or non-operative management may be considered because evidence is limited and the enlarged uterus may make surgery more difficult. <Cite n="1" />
      </P>

      <H2>What If a Stone Is in the Main Bile Duct?</H2>
      <P>Gallbladder surgery does not always solve an obstructed main bile duct immediately.</P>
      <P>
        <strong>ERCP</strong> is an endoscopic procedure that can remove a duct stone, drain infected bile or relieve obstruction. SAGES favours ERCP over surgical common-bile-duct exploration for symptomatic duct stones during pregnancy. <Cite n="1" />
      </P>
      <P>
        ERCP should be therapeutic—not performed casually for diagnosis. MRCP or specialist ultrasound may help establish whether a duct stone is likely first.
      </P>
      <P>
        The team should minimise procedure time and radiation while prioritising effective treatment. Cholangitis and persistent obstruction are dangerous conditions; treatment should not be delayed solely because the woman is pregnant.
      </P>

      <H2>What Should Happen Around Surgery?</H2>
      <P>Care should involve:</P>
      <Bullets
        items={[
          "An experienced surgeon.",
          "Anaesthesia.",
          "The obstetric team.",
          "Fetal assessment appropriate to gestational age.",
          "Thrombosis-risk assessment.",
          "Antibiotics when infection is present.",
          "A plan for preterm-labour symptoms.",
          "Neonatal input when birth could become necessary.",
        ]}
      />
      <P>
        Fetal monitoring depends on gestational age, the operation and available facilities. It should be planned—not added as a ritual that cannot change management.
      </P>

      <H2>Seek Urgent Assessment</H2>
      <P>Attend urgently for:</P>
      <Bullets
        items={[
          "Severe or persistent right-upper-abdominal pain.",
          "Fever or chills.",
          "Yellow eyes or skin.",
          "Dark urine or pale stools.",
          "Repeated vomiting.",
          "Fainting or marked weakness.",
          "Reduced fetal movements.",
          "Contractions or fluid leakage.",
          "Severe headache or visual disturbance.",
          "Feeling seriously unwell.",
        ]}
      />

      <div className="mt-10 bg-surfacealt/60 border-l-2 border-prestige p-6" data-testid="article-takeaway">
        <p className="text-[15px] text-navy leading-relaxed">
          <strong>Dr Tania’s takeaway:</strong> The safest plan is not “never operate during pregnancy” and not “remove every gallbladder immediately.” Uncomplicated pain may allow observation, but cholecystitis, recurrent admission, pancreatitis or bile-duct obstruction can make timely laparoscopy or ERCP safer than repeated delay.
        </p>
      </div>

      <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-16" id="evidence-base">
        Evidence Base
      </h2>
      <ol className="mt-6 space-y-5 list-none pl-0" data-testid="article-references">
        <li id="ref-1" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[1] Kumar SS, Collings AT, Wunker C, et al. SAGES guidelines for the use of laparoscopy during pregnancy. Surgical Endoscopy. 2024;38:2947–2963. doi:10.1007/s00464-024-10810-1.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://pmc.ncbi.nlm.nih.gov/articles/PMC11133165/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://pmc.ncbi.nlm.nih.gov/articles/PMC11133165/</a>
          </p>
        </li>
        <li id="ref-2" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[2] American College of Obstetricians and Gynecologists and American Society of Anesthesiologists. Nonobstetric Surgery During Pregnancy. Committee Opinion No. 775. 2019; reaffirmed 2025.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://www.acog.org/clinical/clinical-guidance/committee-opinion/articles/2019/04/nonobstetric-surgery-during-pregnancy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://www.acog.org/clinical/clinical-guidance/committee-opinion/articles/2019/04/nonobstetric-surgery-during-pregnancy</a>
          </p>
        </li>
        <li id="ref-3" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[3] Koh YX, et al. Optimal treatment strategies for gallbladder disease in pregnancy: a systematic review with dual network meta-analyses. Surgical Endoscopy. 2024;38:7011–7023. doi:10.1007/s00464-024-11336-2.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://pubmed.ncbi.nlm.nih.gov/39406972/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://pubmed.ncbi.nlm.nih.gov/39406972/</a>
          </p>
        </li>
        <li id="ref-4" className="border-l-2 border-prestige pl-5">
          <p className="text-[15px] text-navy leading-relaxed">
            <strong>[4] Tarján D, Szalai EA, Erőss B, et al. Safety and effectiveness of cholecystectomy and endoscopic retrograde cholangiopancreatography in biliary pancreatitis during pregnancy: BORN study. United European Gastroenterology Journal. 2025;13:1803–1811. doi:10.1002/ueg2.70121.</strong>
          </p>
          <p className="mt-1 text-[15px]">
            <a
              href="https://pmc.ncbi.nlm.nih.gov/articles/PMC12606018/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-prestige hover:underline break-all"
            >https://pmc.ncbi.nlm.nih.gov/articles/PMC12606018/</a>
          </p>
        </li>
      </ol>

      <h2 className="font-serif text-2xl md:text-3xl text-navy tracking-tight leading-snug mt-16" id="medical-disclaimer">
        Medical Disclaimer
      </h2>
      <P>
        This Article provides general education and does not replace patient-specific surgical, obstetric, gastroenterology or anaesthetic advice. Do not delay urgent assessment because symptoms have previously settled. Seek emergency care for severe pain, fever, jaundice, repeated vomiting, reduced fetal movements or feeling seriously unwell.
      </P>
    </article>
  );
}
